/**
 * TFL Service Layer
 *
 * Pure API service functions for fetching TFL data.
 * No business logic, no state management - just API calls.
 */

import axios from 'axios'

import { customInstance } from '@/features/auth/axios-config'
import { RequestCreateThreadOrReply } from '@/schema'

import {
  APIPayloadListComments,
  CommentListResponse,
  CreateCommentResponse,
  CreateReplyRequest,
  CreateReplyResponse,
  GetThreadCommentsResponse,
  GetThreadReadResponse,
  TransferCommentPayLoad,
  UpdateCommentRequest,
  UpdateCommentResponse,
} from './types'

const DOCUMENT_COMMENT_BASE_URL = '/document/v1/comments'
const DOCUMENT_THREAD_BASE_URL = '/document/v1/threads'

export const fetchTflHtml = async (
  signedUrl: string,
  signal?: AbortSignal
): Promise<string> => {
  const response = await axios.get<ArrayBuffer>(signedUrl, {
    responseType: 'arraybuffer',
    signal,
    headers: {},
  })

  // Decode the ArrayBuffer to string
  const decoder = new TextDecoder('utf-8')
  return decoder.decode(response.data)
}

/**
 * Creates a new comment for a document version
 * @param data - Comment data with content and document_version_id
 * @returns Promise with created comment data
 */
export const createComment = async (
  data: RequestCreateThreadOrReply
): Promise<CreateCommentResponse> => {
  return customInstance<CreateCommentResponse>({
    method: 'post',
    url: DOCUMENT_COMMENT_BASE_URL,
    data,
  })
}

export const getListComment = async (
  data: APIPayloadListComments
): Promise<CommentListResponse> => {
  return customInstance<CommentListResponse>({
    method: 'post',
    url: `${DOCUMENT_THREAD_BASE_URL}/list`,
    data,
  })
}

export const transferComment = async (
  data: TransferCommentPayLoad
): Promise<void> => {
  return customInstance<void>({
    method: 'post',
    url: `${DOCUMENT_COMMENT_BASE_URL}/transfer`,
    data,
  })
}

/**
 * Get thread with all its comments (root + replies)
 * @param threadId - Thread ID to retrieve
 * @returns Promise with thread metadata and all comments
 */
export const getThreadComments = async (
  threadId: string
): Promise<GetThreadCommentsResponse> => {
  return customInstance<GetThreadCommentsResponse>({
    method: 'get',
    url: `${DOCUMENT_THREAD_BASE_URL}/${threadId}/comments`,
  })
}

export const resolveCommentThread = async (
  threadId: string,
  status: 'resolved' | 'open' = 'resolved'
): Promise<CommentListResponse> => {
  return customInstance<CommentListResponse>({
    method: 'put',
    url: `${DOCUMENT_THREAD_BASE_URL}/${threadId}`,
    data: { status },
  })
}

/**
 * Delete an entire thread and all associated comments (soft delete)
 * @param threadId - Thread ID to delete
 * @returns Promise that resolves when deletion is complete
 */
export const deleteThread = async (threadId: string): Promise<void> => {
  return customInstance<void>({
    method: 'delete',
    url: `${DOCUMENT_THREAD_BASE_URL}/${threadId}`,
  })
}

/**
 * Create a reply to an existing comment
 * @param threadId - Thread ID
 * @param data - Reply data with content and parent_comment_id
 * @returns Promise with created reply data
 */
export const createReply = async (
  threadId: string,
  data: CreateReplyRequest
): Promise<CreateReplyResponse> => {
  return customInstance<CreateReplyResponse>({
    method: 'post',
    url: `${DOCUMENT_THREAD_BASE_URL}/${threadId}/comments`,
    data,
  })
}

/**
 * Update an existing comment's content and mentioned users
 * @param commentId - Comment ID to update
 * @param data - Update data with content and optional mentioned_user_ids
 * @returns Promise with updated comment
 */
export const updateComment = async (
  commentId: string,
  data: UpdateCommentRequest
): Promise<UpdateCommentResponse> => {
  return customInstance<UpdateCommentResponse>({
    method: 'put',
    url: `${DOCUMENT_COMMENT_BASE_URL}/${commentId}`,
    data,
  })
}

/**
 * Delete a reply comment (soft delete)
 * @param commentId - Comment ID to delete
 * @returns Promise that resolves when deletion is complete
 */
export const deleteComment = async (commentId: string): Promise<void> => {
  return customInstance<void>({
    method: 'delete',
    url: `${DOCUMENT_COMMENT_BASE_URL}/${commentId}`,
  })
}

/**
 * Mark a thread as read for the current user
 * @param threadId - Thread ID to mark as read
 * @returns Promise with updated thread read status
 */
export const markThreadAsRead = async (
  threadId: string
): Promise<GetThreadReadResponse> => {
  return customInstance<GetThreadReadResponse>({
    method: 'patch',
    url: `${DOCUMENT_THREAD_BASE_URL}/${threadId}/read`,
  })
}
